import { LazyLoader, showToast } from '@bp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react-lite';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { LessonEditStatus, useLessonsByUuidsQuery } from '../../../types/planung-graphql-client-defs';
import { showSuccessSaveToast } from '../../../utils/toast';
import { FormBlockHeader } from '../../Form/FormBlockHeader';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { teachingBlockLessonToFormType, useTeachingBlockLessons } from '../hooks/useTeachingBlockLessons';
import { VersionLessonFormType } from '../types';
import { TeachingBlockBasicsForm } from './Basics/TeachingBlockBasicsForm';
import { LessonClassesForm } from './Classes/LessonClassesForm';
import { TeachingBlockDurationAndCounter } from './DurationAndCounter/TeachingBlockDurationAndCounter';
import { LessonRoomSupplyForm } from './RoomSupply/LessonRoomSupplyForm';
import { LessonTeacherForm } from './Teachers/LessonTeacherForm';
import { schema } from './validation/schema';

type LessonVersionFormProps = {
  closeForm: () => void;
  lessonUuid?: string | null;
  defaultInitialValues: Partial<VersionLessonFormType>;
  versionUuid: string;
};

export const TeachingBlockLessonForm = observer(
  ({ closeForm, lessonUuid, defaultInitialValues, versionUuid }: LessonVersionFormProps) => {
    const { t } = useTranslation();

    const { createLesson, lessonsData, updateLesson } = useTeachingBlockLessons({
      versionUuid: versionUuid,
    });
    const currentLessonsData = lessonsData.find((lesson) => {
      return lesson.uuid === lessonUuid;
    });

    const [{ data: lessonsQueryData }] = useLessonsByUuidsQuery({
      variables: { uuids: lessonUuid ? [lessonUuid] : [] },
      pause: !lessonUuid,
    });

    const currentLesson = lessonsQueryData?.lessonsByUuids[0];

    const initialValues: VersionLessonFormType = currentLesson
      ? (teachingBlockLessonToFormType(currentLesson) ?? {
          ...defaultInitialValues,
          durationCountArray: [
            {
              duration: 1,
              count: 1,
            },
          ],
        })
      : {
          ...defaultInitialValues,
          teachingLoadEnabled: true,
          durationCountArray: [
            {
              duration: 1,
              count: 1,
            },
          ],
        };

    const handleSubmit = async (values: VersionLessonFormType, formikHelpers: FormikHelpers<VersionLessonFormType>) => {
      const errors: Array<Error> = [];
      if (!values.uuid) {
        const createResult = await createLesson(values);
        if (!createResult || createResult.error) {
          errors.push(createResult.error ?? { name: 'error', message: 'error' });
        }
      } else if (lessonUuid) {
        const updateResult = await updateLesson(values, [lessonUuid]);
        if (!updateResult || updateResult.error) {
          errors.push(updateResult.error ?? { name: 'error', message: 'error' });
        }
      }
      if (errors.length) {
        showToast('Ups.', { type: 'error' });
      } else {
        showSuccessSaveToast();
        formikHelpers.resetForm();
        closeForm();
      }
    };

    const disabled = currentLessonsData?.editStatus === LessonEditStatus.Blocked;

    return (
      <Suspense fallback={<LazyLoader transparent forceHeight='40vh' />}>
        <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
          {(formik) => (
            <Form>
              <div className='form-block'>
                <div className={'form-group'}>
                  <TeachingBlockBasicsForm status={currentLessonsData?.editStatus} timetableDisabled={true} />
                </div>
              </div>
              <div className='form-block'>
                <FormBlockHeader title={t('classes.title', { count: 2 })} />
                <Suspense fallback={<LazyLoader />}>
                  <LessonClassesForm disabled={disabled} />
                </Suspense>
              </div>
              <div className='form-block'>
                <FormBlockHeader title={t('persons.title', { count: 1 })} />
                <Suspense fallback={<LazyLoader />}>
                  <LessonTeacherForm
                    disabled={disabled}
                    defaults={{ writesCertificate: true, teachingLoad: true, present: true }}
                  />
                </Suspense>
              </div>
              {!formik.values.uuid && (
                <div className='form-block'>
                  <FormBlockHeader title={t('lesson.weekCard')} />
                  <Suspense fallback={<LazyLoader />}>
                    <TeachingBlockDurationAndCounter />
                  </Suspense>
                </div>
              )}
              <div className='form-block'>
                <FormBlockHeader title={t('rooms.stock')} />
                <Suspense fallback={<LazyLoader />}>
                  <LessonRoomSupplyForm key={formik.values.subject?.uuid} disabled={disabled} />
                </Suspense>
              </div>
              <ModalBottomButtons
                closeButton={{
                  text: disabled ? t('common.close') : t('common.cancel'),
                  callback: () => {
                    formik.resetForm();
                    closeForm();
                  },
                }}
                submitButton={{ disabled: disabled }}
                isLoading={formik.isSubmitting}
                errors={formik.errors}
              />
            </Form>
          )}
        </Formik>
      </Suspense>
    );
  },
);
